import {
  AddressType,
  UserAccountType,
  BusinessDataV2Type,
  BusinessAddressType,
  BusinessUserV2Client,
} from '@am3nd-org/pointcraft-types';

import { type Employee } from '../schemas/employee';
import { BusinessProfile } from '../types/settings';

/**
 * Maps BusinessAddressType to AddressType
 * BusinessAddressType may have slightly different structure
 */
export function mapBusinessAddressToAddress(address: BusinessAddressType): AddressType {
  return {
    street1: address.street1 || '',
    street2: address.street2 || '',
    city: address.city,
    state: address.state,
    postalCode: address.postalCode || '',
    country: address.country,
  };
}

/**
 * Maps a role string from the user profile to UserAccountType
 */
function mapRoleToUserAccountType(role?: string): UserAccountType {
  if (!role) return UserAccountType.BUSINESS_VIEWER;

  const normalizedRole = role.toUpperCase();

  // Check if it's already a valid UserAccountType value
  if (Object.values(UserAccountType).includes(normalizedRole as UserAccountType)) {
    return normalizedRole as UserAccountType;
  }

  // Legacy mapping for old role strings
  if (normalizedRole === 'ADMIN') return UserAccountType.BUSINESS_ADMIN;
  if (normalizedRole === 'MANAGER') return UserAccountType.BUSINESS_MANAGER;
  if (normalizedRole === 'VIEWER') return UserAccountType.BUSINESS_VIEWER;

  return UserAccountType.BUSINESS_VIEWER;
}

/**
 * Maps SingleUserProfileType (from useDeviceSyncStore) to Employee (settings type)
 */
export function mapBusinessUserToEmployee(user: BusinessUserV2Client): Employee {
  return {
    id: user.id,
    name: user.profile.name,
    email: user.profile.email,
    phone: user.profile.phone,
    role: mapRoleToUserAccountType(user.roleId),
    status: user.isActive ? 'active' : 'inactive',
    permissions: [],
    address: user.profile.address,
    joinedDate: new Date(), // Default to current date since user profile doesn't have this
  };
}

/**
 * Maps an array of SingleUserProfileType to Employee array
 */
export function mapUsersToEmployees(users: BusinessUserV2Client[]): Employee[] {
  return users.map(mapBusinessUserToEmployee);
}

/**
 * Maps the V4 business-data slice to the online-payment settings profile.
 */
export function mapBusinessToBusinessProfile(business: BusinessDataV2Type): BusinessProfile {
  return {
    companyName: business.name,
    email: business.businessInfo.email || '',
    phone: business.businessInfo.phone,
    address: mapBusinessAddressToAddress(business.businessInfo.address),
  };
}
